import { CustomFieldDto, CustomFieldTypes, CustomFieldValues } from "@/store/custom-fields/types";

// @ts-ignore
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import IconUpload from "@/components/IconUpload.vue";
import CustomFieldDatePicker from "@/components/custom-fields/CustomFieldDatePicker.vue";
import { CustomFieldConstants } from "@/mixins/customFieldMixins";
import { Constants } from "@/types/Constants";
import { CustomFieldModalSortBy } from "@/types/sv-data/custom-fields/Constants";

export function MapCustomFieldToModalItem(field: CustomFieldDto, modalItem: ModalItem): ModalItem {
	const newModalItem: ModalItem = {
		key: "cf" + field.id.toString(),
		title: field.title,
		dataType: CustomFieldTypes[field.customFieldTypeId.toString()].toLowerCase(),
		maxLength: field.customFieldTypeId == CustomFieldTypes.Text ? 1000 :
			field.customFieldTypeId == CustomFieldTypes.TextArea ? 10000 :
			field.customFieldTypeId == CustomFieldTypes.Hyperlink ? 10000 : 10,
		min: field.customFieldTypeId == CustomFieldTypes.Number ? -2147483647 : 10,
		max: field.customFieldTypeId == CustomFieldTypes.Number ? 2147483647 : 10,
		order: modalItem.order + (field.order != null ? field.order : 999),
		placeholder: field.placeholder ? field.placeholder : modalItem.placeholder,
		required: field.required,
		readOnly: modalItem.readOnly
	}

	MapAdditionalCustomFieldTypesToModalItem(field.customFieldTypeId, newModalItem, field.id.toString());

	return newModalItem
}

function MapAdditionalCustomFieldTypesToModalItem(type: number, newModalItem: ModalItem,  fieldId: string): void {
	switch (type) {
		case CustomFieldTypes.Date:
			newModalItem.dataType = "component";
			newModalItem.data = CustomFieldDatePicker;
			newModalItem.defaultValue = new Date().toISOString();
			newModalItem.props = { type: "date", inputClass: "datetime-input" };
			break;
		case CustomFieldTypes.Icon:
			newModalItem.dataType = "component";
			newModalItem.data = IconUpload;
			newModalItem.props = { customFieldId: fieldId }
			break;
	}
}

export function hasEmptyRequiredCustomFields(customFieldValues: CustomFieldValues[], customFields: CustomFieldDto[]): boolean {
	if (!customFieldValues || !customFields) {
		return false;
	}

	const requiredFields = customFields.filter(field => field.required);

	const hasEmptyField = requiredFields.some(field => {
		const value = customFieldValues.find(v => v.id === field.id)?.value;
		return !value || value === "";
	});

	return hasEmptyField;
}

export function hasInvalidCustomFieldValues(customFieldValues: CustomFieldValues[], customFields: CustomFieldDto[]): boolean {
	if (!customFieldValues || !customFields) {
		return false;
	}

	return customFields.some(field => {
		const value = customFieldValues.find(v => v.id === field.id)?.value;

		if (value == null) {
			return false;
		}

		switch (field.customFieldTypeId) {
			case CustomFieldTypes.Text:
				return value && value.length > CustomFieldConstants.CF_TEXT_LENGTH;
			case CustomFieldTypes.TextArea:
				return value && value.length > CustomFieldConstants.CF_TEXTAREA_LENGTH;
			case CustomFieldTypes.Hyperlink:
				return value && value.length > CustomFieldConstants.CF_HYPERLINK_LENGTH;
			case CustomFieldTypes.Number: {
				const numValue = Number(value);
				return (
					isNaN(numValue) ||
					numValue < Constants.MINIMUM_INTEGER_VALUE ||
					numValue > Constants.MAXIMUM_INTEGER_VALUE
				);
			}

			default:
				return false;
		}
	});
}

// method used to sort the custom fields modal while taking into account null handling and different data types
export function sortOrderByNullLast(arr: CustomFieldDto[], desc: boolean = false): CustomFieldDto[] {
	return arr.sort((a, b) => {
		// Extract values for the specified key and use optional chaining to prevent null reference exceptions
		const aValue = a?.[CustomFieldConstants.SortByOrder];
		const bValue = b?.[CustomFieldConstants.SortByOrder];

		// If both order values are null the sorting is equal relative to each other to prevent unnecessary reordering
		if (aValue == null && bValue == null) {
			return 0;
		}
		// Ensure null values are placed last in the list to reflect the sort order in the Add Alarm modal
		else if (aValue == null) {
			return 1;
		}
		else if (bValue == null) {
			return -1;
		}
		// Checks if the sorting values are of type number i.e. a column with number values, and then sorts the fields according to the sort order
		else if (typeof aValue === CustomFieldModalSortBy.Number && typeof bValue === CustomFieldModalSortBy.Number) {
			return desc ? bValue - aValue : aValue - bValue;
		}
		// Accounts for other data types or mixed types to maintain sorting consistency
		else {
			return desc ? String(bValue).localeCompare(String(aValue)) : String(aValue).localeCompare(String(bValue));
		}
	});
}
